import "./index.css";
import {
  initBv,
  bvApi,
  type BvSession,
  mountShell,
  statRow,
  dataTable,
  card,
  emptyState,
  pill,
  openModal,
  flash,
  fmtMoney,
  fmtDate,
  skeletonCard,
  h,
} from "./bv-init";

interface Config {
  display_currency: string;
  fx_rate: number;
}
interface Stats {
  issued: number;
  checked_in: number;
  today: number;
  revenue: number;
}
interface Visit {
  id: number;
  visitor_name: string | null;
  contact: string | null;
  code: string;
  amount: number;
  currency: string;
  status: "issued" | "redeemed";
  issued_at: string;
  redeemed_at: string | null;
  pass_url: string;
}
interface Pass {
  id: number;
  name: string;
  kind: string;
  price: number;
  currency: string;
  active: boolean;
}

let session: BvSession;
let currency = "JMD";
let cfg: Config = { display_currency: "USD", fx_rate: 0.0064 };

const conv = (amount: number) => `~${cfg.display_currency} ${(Number(amount) * Number(cfg.fx_rate)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

boot();

async function boot() {
  try {
    session = await initBv();
  } catch (err) {
    renderFatal(err);
    return;
  }
  currency = session.merchant.currency_code || "JMD";
  mountShell({
    brandIcon: "sunrise",
    brandLogo: "/logo.svg",
    title: "Visitor Passes",
    subtitle: `${session.merchant.name || "Your venue"} · tourism & check-in`,
    poweredBy: "Marketplace",
    tabs: [
      { id: "overview", label: "Overview", icon: "sunrise", render: renderOverview },
      { id: "visits", label: "Visits", icon: "ticket", render: renderVisits },
      { id: "passes", label: "Passes", icon: "tag", render: renderPasses },
      { id: "settings", label: "Settings", icon: "settings", render: renderSettings },
    ],
  });
}

function renderFatal(err: unknown) {
  const root = document.getElementById("root")!;
  root.innerHTML = "";
  root.append(
    h(
      "div",
      { class: "bv-fatal" },
      h("strong", null, "Couldn't start"),
      h("p", { class: "bv-muted" }, (err as any)?.message || "No session token found."),
    ),
  );
}

async function renderOverview(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const data = await bvApi<{ config: Config; stats: Stats; recent: Visit[] }>("/api/overview").catch(() => null);
  host.innerHTML = "";
  if (!data) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load", text: "Please try again." }));
    return;
  }
  cfg = data.config;
  const s = data.stats;
  host.append(
    statRow([
      { k: "Passes issued", v: String(s.issued), icon: "ticket" },
      { k: "Checked in", v: String(s.checked_in), icon: "check", tone: "ok" },
      { k: "Issued today", v: String(s.today), icon: "sunrise", tone: Number(s.today) > 0 ? "accent" : undefined },
      { k: "Revenue", v: fmtMoney(Number(s.revenue), currency), d: conv(Number(s.revenue)), icon: "wallet", tone: "accent" },
    ]),
  );

  host.append(
    card({
      title: "Issue passes from sales",
      action: h("button", { class: "primary", onClick: () => doSync(host) }, "Issue from paid orders"),
      body: h("p", { class: "bv-muted" }, `Turn each paid order into a visitor pass with a unique check-in code. Prices also shown in ${cfg.display_currency} for visitors.`),
    }),
  );

  host.append(
    card({
      title: "Recent passes",
      body: data.recent.length
        ? dataTable<Visit>({
            columns: [
              { head: "Visitor", cell: (v) => v.visitor_name || "Guest" },
              { head: "Code", cell: (v) => h("code", null, v.code) },
              { head: "Amount", num: true, cell: (v) => fmtMoney(Number(v.amount), v.currency || currency) },
              { head: "Status", cell: (v) => (v.status === "redeemed" ? pill("checked in", "ok") : pill("issued", "accent")) },
            ],
            rows: data.recent,
          })
        : emptyState({ icon: "ticket", title: "No passes yet", text: "Issue passes from paid orders to begin." }),
    }),
  );
}

async function doSync(host: HTMLElement) {
  const r = await bvApi<{ issued: number }>("/api/sync", { method: "POST" }).catch(() => null);
  if (!r) return flash("Sync failed", "error");
  flash(r.issued ? `Issued ${r.issued} pass${r.issued === 1 ? "" : "es"} from paid orders.` : "No new paid orders.", r.issued ? "success" : "info");
  renderOverview(host);
}

async function renderVisits(host: HTMLElement) {
  host.innerHTML = "";
  let status = "";
  const list = h("div");
  const states = ["", "issued", "redeemed"];
  const labels: Record<string, string> = { "": "All", issued: "Issued", redeemed: "Checked in" };
  const btns = states.map((sv) =>
    h(
      "button",
      {
        class: sv === "" ? "secondary" : "ghost",
        onClick: () => {
          status = sv;
          btns.forEach((bb, i) => {
            (bb as HTMLElement).className = states[i] === status ? "secondary" : "ghost";
          });
          load();
        },
      },
      labels[sv],
    ),
  );
  const seg = h("div", { class: "bv-row" }, ...btns);
  const load = async () => {
    list.innerHTML = "";
    list.append(skeletonCard());
    const d = await bvApi<{ visits: Visit[] }>(`/api/visits${status ? `?status=${status}` : ""}`).catch(() => ({ visits: [] as Visit[] }));
    list.innerHTML = "";
    list.append(
      d.visits.length
        ? dataTable<Visit>({
            columns: [
              { head: "Visitor", cell: (v) => v.visitor_name || "Guest" },
              { head: "Code", cell: (v) => h("code", null, v.code) },
              { head: "Amount", num: true, cell: (v) => fmtMoney(Number(v.amount), v.currency || currency) },
              { head: "Status", cell: (v) => (v.status === "redeemed" ? pill("checked in", "ok") : pill("issued", "accent")) },
            ],
            rows: d.visits,
            rowActions: (v) =>
              h(
                "div",
                { class: "bv-row" },
                h("button", { class: "ghost", onClick: () => showPass(v) }, "Pass"),
                v.status === "issued" ? h("button", { class: "secondary", onClick: () => checkIn(v, load) }, "Check in") : null,
              ),
          })
        : emptyState({ icon: "ticket", title: "No passes", text: "Issue passes from the Overview tab." }),
    );
  };
  host.append(card({ title: "Visitor passes", action: seg, body: list }));
  load();
}

function showPass(v: Visit) {
  openModal({
    title: v.visitor_name || "Visitor pass",
    body: h(
      "div",
      { class: "bv-stack", style: { textAlign: "center" } },
      h("div", { style: { font: "700 28px ui-monospace, monospace", letterSpacing: "2px", color: "var(--accent)", padding: "14px", border: "2px dashed var(--accent)", borderRadius: "12px" } }, v.code),
      h("p", { class: "bv-muted" }, `${fmtMoney(Number(v.amount), v.currency || currency)} · ${conv(Number(v.amount))}`),
      h("p", { class: "bv-muted" }, "Public link: ", h("a", { href: v.pass_url, target: "_blank", rel: "noopener" }, "open pass")),
    ),
    actions: [{ label: "Close" }],
  });
}

async function checkIn(v: Visit, reload: () => void) {
  const ok = await bvApi(`/api/visits/${v.id}/redeem`, { method: "POST" }).then(() => true).catch(() => false);
  flash(ok ? `${v.visitor_name || "Visitor"} checked in.` : "Couldn't check in", ok ? "success" : "error");
  if (ok) reload();
}

async function renderPasses(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const d = await bvApi<{ passes: Pass[] }>("/api/passes").catch(() => ({ passes: [] as Pass[] }));
  host.innerHTML = "";
  host.append(
    card({
      title: "Pass types",
      action: h("button", { class: "primary", onClick: () => openPass(host) }, "New pass"),
      body: d.passes.length
        ? dataTable<Pass>({
            columns: [
              { head: "Name", cell: (p) => p.name },
              { head: "Kind", cell: (p) => p.kind },
              { head: "Price", num: true, cell: (p) => `${fmtMoney(Number(p.price), p.currency || currency)} · ${conv(Number(p.price))}` },
              { head: "Status", cell: (p) => (p.active ? pill("active", "ok") : pill("off", "")) },
            ],
            rows: d.passes,
            rowActions: (p) => h("button", { class: "ghost", onClick: () => openPass(host, p) }, "Edit"),
          })
        : emptyState({ icon: "tag", title: "No pass types", text: "Define day passes, tours or tickets you sell." }),
    }),
  );
}

function openPass(host: HTMLElement, pass?: Pass) {
  const name = h("input", { type: "text", value: pass?.name || "" }) as HTMLInputElement;
  const kind = h("select", null, ...["pass", "tour", "ticket"].map((k) => h("option", { value: k }, k))) as HTMLSelectElement;
  kind.value = pass?.kind || "pass";
  const price = h("input", { type: "number", min: "0", step: "0.01", value: String(pass?.price ?? 0) }) as HTMLInputElement;
  let close = () => {};
  const handle = openModal({
    title: pass ? "Edit pass" : "New pass",
    body: h("div", { class: "bv-stack" }, field("Name", name), field("Kind", kind), field(`Price (${currency})`, price)),
    actions: [
      { label: "Cancel" },
      {
        label: "Save",
        primary: true,
        onClick: () => {
          if (!name.value.trim()) {
            flash("Name required", "error");
            return true;
          }
          bvApi("/api/passes", {
            method: "POST",
            body: JSON.stringify({ id: pass?.id, name: name.value.trim(), kind: kind.value, price: +price.value }),
          })
            .then(() => {
              close();
              flash("Pass saved", "success");
              renderPasses(host);
            })
            .catch((e) => flash(e?.message || "Failed", "error"));
          return true;
        },
      },
    ],
  });
  close = handle.close;
}

async function renderSettings(host: HTMLElement) {
  host.innerHTML = "";
  host.append(skeletonCard());
  const c = (await bvApi<{ config: Config }>("/api/config").catch(() => null))?.config;
  host.innerHTML = "";
  if (!c) {
    host.append(emptyState({ icon: "alert", title: "Couldn't load settings" }));
    return;
  }
  cfg = c;
  const dc = h("input", { type: "text", value: c.display_currency, maxLength: "6" }) as HTMLInputElement;
  const fx = h("input", { type: "number", min: "0", step: "0.0001", value: String(c.fx_rate) }) as HTMLInputElement;
  const save = h(
    "button",
    {
      class: "primary",
      onClick: async () => {
        const r = await bvApi<{ config: Config }>("/api/config", {
          method: "POST",
          body: JSON.stringify({ display_currency: dc.value.trim(), fx_rate: +fx.value }),
        }).catch(() => null);
        if (r) cfg = r.config;
        flash(r ? "Settings saved" : "Save failed", r ? "success" : "error");
      },
    },
    "Save settings",
  );
  host.append(
    card({
      title: "Visitor currency",
      action: save,
      body: h(
        "div",
        { class: "bv-stack" },
        field("Display currency", dc),
        field(`1 ${currency} = ? ${c.display_currency} (FX rate)`, fx),
        h("p", { class: "bv-muted" }, `Visitors see prices in ${currency} and an approximate ${c.display_currency} amount. Update the rate as needed.`),
      ),
    }),
  );
}

function field(label: string, input: HTMLElement): HTMLElement {
  return h("div", { class: "bv-field" }, h("label", { class: "bv-label" }, label), input);
}
